import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
} from 'react';

/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hook, and context co-located */

export interface TableRefs {
  placeholderRef: React.RefObject<HTMLDivElement | null>;
  tableRef: React.RefObject<HTMLDivElement | null>;
}

interface TableScrollContextValue {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  registerTable: (id: string, refs: TableRefs) => void;
  unregisterTable: (id: string) => void;
}

const TableScrollContext = createContext<TableScrollContextValue | null>(null);

// No-op implementation for fallback when context is not available
const noopContextValue: TableScrollContextValue = {
  scrollContainerRef: { current: null },
  registerTable: () => {},
  unregisterTable: () => {},
};

export function useTableScroll(): TableScrollContextValue {
  const context = useContext(TableScrollContext);
  // Return no-op implementation for graceful fallback if used outside provider
  return context ?? noopContextValue;
}

interface TableScrollProviderProps {
  scrollContainerRef: React.RefObject<HTMLDivElement | null>;
  children: React.ReactNode;
}

export function TableScrollProvider({
  scrollContainerRef,
  children,
}: TableScrollProviderProps) {
  const tablesRef = useRef<Map<string, TableRefs>>(new Map());
  const rafPendingRef = useRef(false);
  // Track last applied transform values to prevent redundant DOM writes that cause flashing
  const lastTransformRef = useRef<Map<string, string>>(new Map());

  const updateTablePositions = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }
    const containerRect = container.getBoundingClientRect();
    const { scrollTop } = container;

    // Batch all reads first
    const updates: Array<{
      tableEl: HTMLDivElement;
      transform: string;
      id: string;
    }> = [];
    tablesRef.current.forEach((refs, id) => {
      if (!refs.placeholderRef.current || !refs.tableRef.current) {
        return;
      }
      const placeholderRect =
        refs.placeholderRef.current.getBoundingClientRect();
      const top = placeholderRect.top - containerRect.top + scrollTop;
      // Round to avoid floating point precision issues
      const transform = `translateY(${Math.round(top)}px)`;
      updates.push({ tableEl: refs.tableRef.current, transform, id });
    });

    // Batch all writes - skip if transform hasn't changed
    updates.forEach(({ tableEl, transform, id }) => {
      const lastTransform = lastTransformRef.current.get(id);
      if (transform !== lastTransform) {
        tableEl.style.transform = transform;
        lastTransformRef.current.set(id, transform);
      }
    });
  }, [scrollContainerRef]);

  const handleScroll = useCallback(() => {
    if (rafPendingRef.current) {
      return;
    }
    rafPendingRef.current = true;
    requestAnimationFrame(() => {
      rafPendingRef.current = false;
      updateTablePositions();
    });
  }, [updateTablePositions]);

  // Schedule position update via RAF (debounced)
  const scheduleUpdate = useCallback(() => {
    if (rafPendingRef.current) {
      return;
    }
    rafPendingRef.current = true;
    requestAnimationFrame(() => {
      rafPendingRef.current = false;
      updateTablePositions();
    });
  }, [updateTablePositions]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) {
      return;
    }

    // Update on scroll
    container.addEventListener('scroll', handleScroll, { passive: true });

    // Update on layout changes (content size changes)
    // Guard for SSR/jsdom environments that lack ResizeObserver
    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        scheduleUpdate();
      });
      resizeObserver.observe(container);
    }

    // Update when DOM content changes (new messages, images load, etc.)
    // This catches content reflows that don't change container size
    const mutationObserver = new MutationObserver(() => {
      scheduleUpdate();
    });
    mutationObserver.observe(container, {
      childList: true,
      subtree: true,
    });

    // Update on window resize (viewport changes)
    const handleWindowResize = () => scheduleUpdate();
    window.addEventListener('resize', handleWindowResize, { passive: true });

    // Initial position update
    scheduleUpdate();

    return () => {
      container.removeEventListener('scroll', handleScroll);
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', handleWindowResize);
    };
  }, [scrollContainerRef, handleScroll, scheduleUpdate]);

  const registerTable = useCallback(
    (id: string, refs: TableRefs) => {
      tablesRef.current.set(id, refs);
      // Update positions immediately so new tables are positioned correctly
      scheduleUpdate();
    },
    [scheduleUpdate]
  );

  const unregisterTable = useCallback((id: string) => {
    tablesRef.current.delete(id);
    lastTransformRef.current.delete(id);
  }, []);

  const contextValue: TableScrollContextValue = {
    scrollContainerRef,
    registerTable,
    unregisterTable,
  };

  return (
    <TableScrollContext.Provider value={contextValue}>
      {children}
    </TableScrollContext.Provider>
  );
}
